import React, { useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import CloseIcon from "@mui/icons-material/Close";
import {
  TextField,
  Button,
  Grid,
  Typography,
  Box,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
  useTheme,
} from "@mui/material";

import { change_Password } from "../../services/users.services";
import { tokens } from "../../theme";
import { useAuth } from "../../AuthContext";
import { useNavigate } from "react-router-dom";

const validationSchema = Yup.object({
  oldPassword: Yup.string().required("Old Password is required"),
  newPassword: Yup.string()
    .required("New Password is required")
    .min(6, "Password must be at least 6 characters")
    .max(20, "Password must be at most 20 characters")
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])/,
      "Password must include at least one uppercase letter, one lowercase letter, one number, and one special character"
    )
    .notOneOf(
      [Yup.ref("oldPassword")],
      "New Password must be different from Old Password"
    ),
  confirmPassword: Yup.string()
    .required("Confirm Password is required")
    .oneOf([Yup.ref("newPassword"), null], "Passwords must match"),
});


const ChangePassword = ({ open, onClose, mandatory = false, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [showMessage, setShowMessage] = useState(false);
  const navigate = useNavigate();

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { getUserDetails, logout } = useAuth();
  const User = getUserDetails();
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);


  const handleLogoutAndRedirect = async () => {
    await logout();
    navigate("/login");
  };

  const handleSubmit = async (
    { email, oldPassword, newPassword, confirmPassword, isCustomer },
    { resetForm } // Formik helpers object (was "resetform", which is not a function)
  ) => {
    console.log(oldPassword);
    setLoading(true);
    setErrorMessage("");
    try {
      const currentUser = await getUserDetails();
      const response = await change_Password({
        email: currentUser?.Email,
        oldPassword: oldPassword,
        newPassword: newPassword,
        isCustomer: false,
      });
      const { isSuccess, message, result } = response;
      if (isSuccess) {
        setSuccessMessage(result);
        setShowMessage(true);

      
        resetForm();

        if (mandatory) {
          setTimeout(() => {
            handleLogoutAndRedirect();
          }, 4000);
        }
      } else {
        setErrorMessage(message);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
      setTimeout(() => {
        setErrorMessage("");
        setSuccessMessage("");
        setShowMessage(false); // unlock the fields again after the message is gone
      }, 4500);
    }
  };

  return (
    <Dialog
      open={open}
      maxWidth="md"
      onClose={(event, reason) => {
        if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
          console.log("Closed");
        }
      }}
      sx={{
     
        ...(mandatory && {
          "& .MuiBackdrop-root": {
            backdropFilter: "blur(8px)",
            backgroundColor: "rgba(0, 0, 0, 0.45)",
          },
        }),
        "& .MuiDialog-paper": {
          padding: 2,
          borderRadius: 3,
          backgroundColor: "#f2f0f0",
          margin: "auto",
        },
      }}
    >
      <DialogTitle
        fontWeight="bold"
        variant="h4"
        textAlign="center"
        sx={{
          backgroundColor: "#f2f0f0",
          color: "#666666",
          padding: 2,
          borderRadius: 1,
          marginBottom: 3,
        }}
      >
        Change Password

        
        {!mandatory && (
          <IconButton
            aria-label="close"
            onClick={() => {
              onClose();
            }}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: "#B20021",
            }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>
      <DialogContent>
       
        {mandatory && (
                    <Box mb={2} display="flex" justifyContent="center" sx={{ width: "400px" }}>
            <Typography
              sx={{ fontSize: "13px" }}
              color="#B20021"
              textAlign="center"
            >
              Your password has expired as per the password expiration policy. Please set a new password to continue.
            </Typography>
          </Box>
        )}
        <Box sx={{ width: "400px" }}>
          <Formik
            initialValues={{
              oldPassword: "",
              newPassword: "",
              email: "",
              isCustomer: true,
              confirmPassword: "",
            }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ resetForm, errors, touched, handleBlur, isValid, dirty }) => (
              <Form>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={12}>
                    <Field
                      as={TextField}
                      sx={{ mt: 2 }}
                      type={showOldPassword ? "text" : "password"}
                      name="oldPassword"
                      label="Old Password"
                      fullWidth
                      disabled={showMessage}
                      onClick={() => setErrorMessage("")}
                      onBlur={handleBlur}
                      error={touched.oldPassword && !!errors.oldPassword}
                      helperText={touched.oldPassword && errors.oldPassword}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={() =>
                                setShowOldPassword(!showOldPassword)
                              }
                              edge="end"
                            >
                              {showOldPassword ? (
                                <VisibilityIcon sx={{ color: "#ad868d;" }} />
                              ) : (
                                <VisibilityOffIcon sx={{ color: "#ad868d;" }} />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Field
                      as={TextField}
                      type={showNewPassword ? "text" : "password"}
                      name="newPassword"
                      label="New Password"
                      fullWidth
                      inputProps={{ maxLength: 20 }}
                      disabled={showMessage}
                      onClick={() => setErrorMessage("")}
                      onBlur={handleBlur}
                      error={touched.newPassword && !!errors.newPassword}
                      helperText={touched.newPassword && errors.newPassword}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={() =>
                                setShowNewPassword(!showNewPassword)
                              }
                              edge="end"
                            >
                              {showNewPassword ? (
                                <VisibilityIcon sx={{ color: "#ad868d;" }} />
                              ) : (
                                <VisibilityOffIcon sx={{ color: "#ad868d;" }} />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Field
                      as={TextField}
                      type={showConfirmPassword ? "text" : "password"}
                      name="confirmPassword"
                      label="Confirm Password"
                      fullWidth
                      disabled={showMessage}
                      onClick={() => setErrorMessage("")}
                      onBlur={handleBlur}
                      error={
                        touched.confirmPassword && !!errors.confirmPassword
                      }
                      helperText={
                        touched.confirmPassword && errors.confirmPassword
                      }
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={() =>
                                setShowConfirmPassword(!showConfirmPassword)
                              }
                              edge="end"
                            >
                              {showConfirmPassword ? (
                                <VisibilityIcon sx={{ color: "#ad868d;" }} />
                              ) : (
                                <VisibilityOffIcon sx={{ color: "#ad868d;" }} />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>

                 
                  <Grid
                    item
                    xs={12}
                    sx={{
                      display: "flex",
                      justifyContent: mandatory ? "space-between" : "center",
                      mt: 1,
                    }}
                  >
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      sx={{
                        backgroundColor: colors.agColors["red-darker"],
                        borderColor: colors.agColors["red-darker"],
                        color: colors.agColors["white"],
                        fontSize: "14px",
                        fontWeight: "bold",
                        padding: "10px 20px",
                      }}
                      disabled={loading || !dirty || showMessage}
                    >
                      {loading ? <CircularProgress size={20} /> : "Save"}
                    </Button>

                  
                    {mandatory && (
                      <Button
                        variant="outlined"
                        onClick={handleLogoutAndRedirect}
                        sx={{
                          borderColor: colors.agColors["red-darker"],
                          color: colors.agColors["red-darker"],
                          fontSize: "14px",
                          fontWeight: "bold",
                          padding: "10px 20px",
                        }}
                      >
                        Logout
                      </Button>
                    )}
                  </Grid>
                </Grid>
              </Form>
            )}
          </Formik>
        </Box>
        {successMessage.trim() !== "" && (
          <Box
            mt={2}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Typography variant="body1" color="#B20021">
              {successMessage}
            </Typography>
          </Box>
        )}
        {errorMessage.trim() !== "" && (
          <Box
            mt={2}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Typography variant="body1" color="#B20021">
              {errorMessage}
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ChangePassword;